import { createSlice } from "@reduxjs/toolkit";

// Mirrors the backend ordering (`order_by("pinned_at", "-created_at")` on
// Postgres, NULLs last): pinned notes first, oldest pin on top, then the
// newest notes first (`ordering=oldest`: oldest first, i.e. "created_at").
const compareNotes = (ordering) => (a, b) => {
  const aPinned = a.pinned_at ? new Date(a.pinned_at).getTime() : null;
  const bPinned = b.pinned_at ? new Date(b.pinned_at).getTime() : null;
  if (aPinned !== null && bPinned === null) return -1;
  if (aPinned === null && bPinned !== null) return 1;
  if (aPinned !== null && bPinned !== null && aPinned !== bPinned) {
    return aPinned - bPinned;
  }
  const createdDiff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  return ordering === "oldest" ? -createdDiff : createdDiff;
};

const sortNotes = (notes, ordering) => [...notes].sort(compareNotes(ordering));

export const notesSlice = createSlice({
  name: "notes",
  initialState: {
    notes: [],
    selectedNote: null,
    // Order of the loaded list: "newest" (API default) or "oldest"
    ordering: "newest",
    pagination: {
      next: null,
      previous: null,
      count: 0,
    },
  },
  reducers: {
    setNotes: (state, action) => {
      state.notes = action.payload.data || action.payload;
      state.pagination = action.payload.pagination || {
        next: null,
        previous: null,
        count: 0,
      };
    },
    appendNotes: (state, action) => {
      const loadedIds = new Set(state.notes.map((note) => note.id));
      const newNotes = action.payload.data.filter((note) => !loadedIds.has(note.id));
      state.notes = [...state.notes, ...newNotes];
      state.pagination = action.payload.pagination || {
        next: null,
        previous: null,
        count: 0,
      };
    },
    setNotesOrdering: (state, action) => {
      state.ordering = action.payload === "oldest" ? "oldest" : "newest";
    },
    clearNotes: (state) => {
      state.notes = [];
      state.selectedNote = null;
      state.pagination = {
        next: null,
        previous: null,
        count: 0,
      };
    },
    addNote: (state, action) => {
      state.notes = sortNotes([action.payload, ...state.notes], state.ordering);
      if (typeof state.pagination.count === "number") {
        state.pagination.count += 1;
      }
    },
    updateNote: (state, action) => {
      const { id, updates } = action.payload;
      const noteIndex = state.notes.findIndex(note => note.id === id);
      if (noteIndex !== -1) {
        state.notes[noteIndex] = { ...state.notes[noteIndex], ...updates };
        state.notes = sortNotes(state.notes, state.ordering);
      }
    },
    deleteNote: (state, action) => {
      const id = action.payload;
      const before = state.notes.length;
      state.notes = state.notes.filter(note => note.id !== id);
      if (state.notes.length < before && state.pagination.count) {
        state.pagination.count -= 1;
      }
      if (state.selectedNote?.id === id) {
        state.selectedNote = null;
      }
    },
    selectNote: (state, action) => {
      state.selectedNote = action.payload;
    }
  }
});

export const {
  setNotes,
  setNotesOrdering,
  appendNotes,
  clearNotes,
  addNote,
  updateNote,
  deleteNote,
  selectNote,
} = notesSlice.actions;
export default notesSlice.reducer;
